/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { GoogleGenAI, GenerateContentResponse } from '@google/genai';
import { ChatHistory } from '../../types/chat';
import { AVAILABLE_MODELS, AiProvider } from '../../lib/ai/models';

/**
 * Vercel Gateway API Simulation
 * 
 * In a real Next.js application, this would be `app/api/chat/route.ts` using the Vercel AI SDK
 * to route requests to OpenAI, Anthropic, or Google.
 * 
 * Since this is a client-side demo, we:
 * 1. Use the real Google GenAI SDK if the key is present.
 * 2. Simulate the streaming response for OpenAI/Anthropic/DeepSeek to demonstrate UI capability
 *    (as we cannot expose secret keys for those providers in the browser).
 */

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const streamChatResponse = async function* (
    history: ChatHistory,
    systemInstruction?: string,
    modelId: string = 'gemini-2.5-flash'
): AsyncGenerator<any> {
    
    // 1. Identify Provider
    const modelConfig = AVAILABLE_MODELS.find(m => m.id === modelId);
    const provider = modelConfig?.provider || 'Google';

    // 2. Route Request
    if (provider === 'Google') {
        if (!process.env.API_KEY) {
            throw new Error("Gemini API_KEY environment variable not set.");
        }
        const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
        
        const sdkHistory = history.slice(0, -1).map(msg => ({
            role: msg.role,
            parts: [{ text: msg.content }]
        }));
        
        const newMessage = history[history.length - 1].content;

        const chat = ai.chats.create({
            model: modelId,
            history: sdkHistory,
            config: { systemInstruction }
        });

        const result = await chat.sendMessageStream({ message: newMessage });

        for await (const chunk of result) {
            // Unify response format
            yield { text: chunk.text };
        }

    } else {
        // --- GATEWAY SIMULATION FOR OTHER PROVIDERS ---
        // In a real Vercel deployment, this calls: await openai.chat.completions.create({ stream: true ... })
        
        yield { text: `[${provider} Gateway] Connecting to ${modelId}...` };
        await sleep(500);
        yield { text: "\n\n" }; // New line

        const mockResponse = `This is a simulated response from **${modelConfig?.name}** via the Vercel Gateway.\n\nSince this is a client-side demo, we cannot securely store the ${provider} API Secret Key in the browser. \n\nIn a production environment, this would route through a Next.js Edge Function using the Vercel AI SDK to stream real tokens from ${provider}.`;
        
        const words = mockResponse.split(" ");
        for (const word of words) {
            await sleep(30 + Math.random() * 50); // Simulate token generation latency
            yield { text: word + " " };
        }
    }
};

/**
 * @deprecated Use streamChatResponse instead
 */
export const streamGeminiResponse = streamChatResponse;